import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import SEO from '../components/SEO';
import cafGroupLogo from '../assets/logos/landing/logo-color.png';

const HomePage = () => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  
  // Blue-based colors for CAF Group (matching fence branding as primary business)
  const colors = {
    primary: '#4A7C9B',
    accent: isDark ? '#6BA3C7' : '#4A7C9B',
    text: isDark ? '#f1f5f9' : '#0f172a',
    textMuted: isDark ? '#94a3b8' : '#64748b',
    bg: isDark ? '#0f172a' : '#f8fafc',
    surface: isDark ? '#1e293b' : '#ffffff',
    border: isDark ? '#334155' : '#e2e8f0',
    // Division-specific colors
    fenceBlue: '#4A7C9B',
    propertyBlue: '#456CAC',
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: colors.bg }}>
      <SEO 
        title="CAF Group of Companies | Central Alberta Fencing & Property Services"
        description="CAF Group of Companies - Your local experts for fencing supplies, installation, and property services in Central Alberta. VEKA & Catalyst authorized dealer."
      />

      {/* Hero Section */}
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <header className="py-6 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto flex justify-between items-center">
            <div className="flex items-center gap-3">
              <img 
                src={cafGroupLogo} 
                alt="CAF Group of Companies" 
                className="h-14 w-auto"
              />
            </div>
            <div className="hidden sm:flex items-center gap-4 text-sm" style={{ color: colors.textMuted }}>
              <a href="tel:4039865484" className="hover:text-[#4A7C9B] transition-colors">
                (403) 986-5484
              </a>
              <span>|</span>
              <a href="mailto:hello@cafinc.ca" className="hover:text-[#4A7C9B] transition-colors">
                hello@cafinc.ca
              </a>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8 py-12">
          <div className="max-w-5xl mx-auto text-center">
            {/* Title */}
            <div className="mb-12">
              <p 
                className="text-xs font-bold uppercase tracking-widest mb-4"
                style={{ color: colors.accent }}
              >
                Proudly Serving Central Alberta Since 2009
              </p>
              <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-6">
                Welcome to
                <br />
                <span style={{ color: '#4A7C9B' }}>CAF Group of Companies</span>
              </h2>
              <p className="text-lg sm:text-xl max-w-2xl mx-auto" style={{ color: colors.textMuted }}>
                Your trusted local partner for quality fencing supplies and professional property services.
              </p>
            </div>

            {/* Two Division Cards */}
            <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
              {/* Fencing Division */}
              <Link 
                to="/fence"
                className="group relative overflow-hidden rounded-3xl p-8 lg:p-10 text-left transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl"
                style={{ 
                  backgroundColor: '#456CAC',
                  boxShadow: '0 25px 50px -12px rgba(69, 108, 172, 0.25)'
                }}
              >
                <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2 group-hover:scale-150 transition-transform duration-700" />
                <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2 group-hover:scale-150 transition-transform duration-700" />
                
                <div className="relative z-10">
                  <div className="w-16 h-16 rounded-2xl bg-white/10 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                    <svg viewBox="0 0 24 24" className="w-8 h-8 text-white" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M3 21V8l3-3v16M9 21V5l3-3v19M15 21V5l3-3v19M21 21V8l-3-3" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M3 12h18M3 16h18" strokeLinecap="round"/>
                    </svg>
                  </div>
                  
                  <h3 className="font-heading text-2xl lg:text-3xl font-bold text-white mb-3">
                    Central Alberta Fencing
                  </h3>
                  <p className="text-white/80 mb-6 text-sm lg:text-base">
                    Premium fence supplies, materials, custom fabrication, and professional installation services. VEKA & Catalyst authorized dealer.
                  </p>
                  
                  <div className="flex flex-wrap gap-2 mb-6">
                    <span className="px-3 py-1 bg-white/10 rounded-full text-xs text-white/90">
                      250+ Products
                    </span>
                    <span className="px-3 py-1 bg-white/10 rounded-full text-xs text-white/90">
                      Local Delivery
                    </span>
                    <span className="px-3 py-1 bg-white/10 rounded-full text-xs text-white/90">
                      Expert Advice
                    </span>
                  </div>
                  
                  <div className="flex items-center gap-2 text-white font-semibold group-hover:gap-4 transition-all">
                    <span>Explore Fencing</span>
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </Link>

              {/* Property Services Division */}
              <Link 
                to="/ps"
                className="group relative overflow-hidden rounded-3xl p-8 lg:p-10 text-left transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl"
                style={{ 
                  backgroundColor: '#4B5563',
                  boxShadow: '0 25px 50px -12px rgba(75, 85, 99, 0.25)'
                }}
              >
                <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2 group-hover:scale-150 transition-transform duration-700" />
                <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2 group-hover:scale-150 transition-transform duration-700" />
                
                <div className="relative z-10">
                  <div className="w-16 h-16 rounded-2xl bg-white/10 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                    {/* Custom Property Services icon - house with tree/landscape */}
                    <svg viewBox="0 0 24 24" className="w-8 h-8 text-white" fill="none" stroke="currentColor" strokeWidth="1.5">
                      {/* House */}
                      <path d="M3 10.5L12 3l9 7.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M5 9v10h14V9" strokeLinecap="round" strokeLinejoin="round"/>
                      {/* Tree */}
                      <path d="M18 21v-4" strokeLinecap="round"/>
                      <path d="M18 17c-1.5 0-2.5-1.5-2.5-3s1-3 2.5-3 2.5 1.5 2.5 3-1 3-2.5 3z" strokeLinecap="round" strokeLinejoin="round"/>
                      {/* Grass/lawn lines */}
                      <path d="M1 21h4M7 21h3" strokeLinecap="round"/>
                    </svg>
                  </div>
                  
                  <h3 className="font-heading text-2xl lg:text-3xl font-bold text-white mb-3">
                    CAF Property Services
                  </h3>
                  <p className="text-white/80 mb-6 text-sm lg:text-base">
                    Complete property maintenance and improvement services for residential and commercial properties across Central Alberta.
                  </p>
                  
                  <div className="flex flex-wrap gap-2 mb-6">
                    <span className="px-3 py-1 bg-white/10 rounded-full text-xs text-white/90">
                      Landscaping
                    </span>
                    <span className="px-3 py-1 bg-white/10 rounded-full text-xs text-white/90">
                      Snow Removal
                    </span>
                    <span className="px-3 py-1 bg-white/10 rounded-full text-xs text-white/90">
                      Maintenance
                    </span>
                  </div>
                  
                  <div className="flex items-center gap-2 text-white font-semibold group-hover:gap-4 transition-all">
                    <span>Explore Property Services</span>
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </Link>
            </div>

            {/* Quick Contact */}
            <div className="mt-12 pt-8 border-t" style={{ borderColor: colors.border }}>
              <p className="text-sm mb-4" style={{ color: colors.textMuted }}>
                Not sure which service you need? Give us a call!
              </p>
              <a 
                href="tel:4039865484"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold transition-all hover:-translate-y-0.5"
                style={{ backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}
              >
                <span>(403) 986-5484</span>
              </a>
            </div>
          </div>
        </main>

        {/* Footer */}
        <footer className="py-6 px-4 text-center text-sm" style={{ color: colors.textMuted }}>
          <p>&copy; {new Date().getFullYear()} CAF Group of Companies. All rights reserved.</p>
          <div className="flex justify-center gap-4 mt-2">
            <Link to="/privacy" className="hover:text-[#4A7C9B] transition-colors">Privacy Policy</Link>
            <span>|</span>
            <Link to="/terms" className="hover:text-[#4A7C9B] transition-colors">Terms of Service</Link>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default HomePage;
